import { useMutation, useQuery } from "@apollo/client";
import WarningAmber from "@mui/icons-material/WarningAmber";
import { Box, Chip, Grid, Skeleton, Stack, Tooltip, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ChemicalPicker } from "components/ChemicalPicker";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import { CheckboxField } from "components/Forms/CheckboxField";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { StateFieldsForm } from "components/Forms/StateFieldsForm";
import { CatalogLink } from "components/ModelLinks/CatalogLink";
import { AmountCodeSelect } from "components/OverallStorage/AmountCodeSelect";
import {
  FacilityChemicalStorageLocationInputWithIssues,
  StorageLocationsDataGrid,
} from "components/StorageLocationsDataGrid";
import { ThresholdChip } from "components/ThresholdChip";
import { LEGACY_FIELD_SUFFIX } from "encamp-shared/src/constants";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import {
  CHEMICAL_EXCLUSION_HAZARDS_TOOLTIP,
  chemicalExclusionWarning,
} from "encamp-shared/src/tierii/chemicalExclusionWarning";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { storageTypeFields } from "encamp-shared/src/utils/storageTypeFields";
import { gql } from "generated-graphql";
import {
  DynamicField,
  FacilityChemicalDetailsFragment,
  FacilityChemicalInput,
  FacilityChemicalStorageLocationInput,
  Issue,
  PreviousYearValuesQuery,
  ProgramArea,
  UnitType,
} from "generated-graphql/graphql";
import { transform } from "hooks/transform/transformFacilityChemical";
import { useDebounce } from "hooks/useDebounce";
import { useTenant } from "hooks/useTenant";
import { useValidatingForm } from "hooks/useValidatingForm";
import invariant from "invariant";
import { omit } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, SubmitHandler, useFieldArray } from "react-hook-form";
import { GetReportDetailsQueryName } from "routes/Customer/Chemicals/Report/useReport";
import { useFacilityChemicalInputValidation } from "routes/Customer/Chemicals/Report/validationHooks/useFacilityChemicalInputValidation";
import { useFacility } from "routes/Customer/Facility/useFacility";
import { hasCriticalIssues } from "util/forms";
import { v4 as uuid } from "uuid";

import { getCaAmountTooltip } from "../../caAmountTooltips";

gql(`
  fragment FacilityChemicalChemical on Chemical {
    id
    ...ChemicalPicker
    alternateId
    isEhs
    noHazardsNotReporting
    physicalHazards
    healthHazards
  }
`);

gql(`
  fragment FacilityChemicalDetails on FacilityChemical {
      id
      reportingYear
      unit
      hasConfidentialStorageLocations
      isAlwaysReported
      facility {
        id
        name
        state
      }
      chemical {
        ...FacilityChemicalChemical
      }
      ...FacilityChemicalAggregate
      storageLocations {
        ...FacilityChemicalStorageLocationForm
      }
      issues {
        ...issue
      }
      stateFields {
        key
        value
        type
        jurisdiction
        inputType
        label
        tooltip
        selectOptions {
          label
          value
        }
        group
        visibleWhen
      }
      jurisdictions
  }
`);

const FACILITY_CHEMICAL_DETAILS = gql(`
  query FacilityChemicalDetails ($id: ID!) {
    facilityChemical(id: $id) {
      ...FacilityChemicalDetails
    }
  }
`);

const EDIT_FACILITY_CHEMICAL = gql(`
  mutation EditFacilityChemical($id: ID, $input: FacilityChemicalInput!, $reportId: ID) {
    upsertFacilityChemical(id: $id, input: $input, reportId: $reportId) {
      ...FacilityChemicalDetails
    }
  }
`);

const PREVIOUS_YEAR_DETAILS_QUERY =
  gql(`query PreviousYearValues($facilityId: ID!, $chemicalId: ID!, $reportingYear: Int!) {
  facilityChemicalByYear(facilityId: $facilityId, chemicalId: $chemicalId, reportingYear: $reportingYear) {
    maxAmount
    unit
  }
}`);

const IS_OVER_THRESHOLD_QUERY = gql(`
  query IsChemicalOverThreshold($input: FacilityChemicalInput!) {
    getFacilityChemicalThresholdData(input: $input) {
      isOverThreshold
      indeterminateReason
      thresholdResults {
        description
        threshold
        unit
        overThreshold
        isSection312
        isSection302
        type
        casNumber
      }
    }
}`);

const FACILITY_CHEMICAL_STATE_FIELDS_QUERY = gql(`
  query GetFacilityChemicalStateFields($facilityId: ID!) {
    facilityChemicalStateFieldsForFacility(facilityId: $facilityId) {
      items {
        key
        value
        jurisdiction
        label
        tooltip
        type
        inputType
        selectOptions {
          label
          value
        }
        group
        visibleWhen
      }
    }
}`);

interface AddEditFacilityChemicalDialogProps {
  facilityChemicalId?: string;
  reportId?: string;
  reportingYear: number;
  alreadySelectedChemicalIds?: string[];
  facilityId: string;
  onClose: () => void;
}

export type FacilityChemicalDetailFormType = Omit<
  FacilityChemicalInput,
  "maxAmountCode" | "averageAmountCode"
> & {
  id?: string;
  maxAmountCode: string | number | null;
  averageAmountCode: string | number | null;
  stateFields: (DynamicField & { type: ProgramArea })[];
  facility?: { state?: string | null } | null;
};

export type FacilityChemicalStorageLocationDetailFormType = FacilityChemicalStorageLocationInput & {
  id?: string;
};

function toNumericOrZero(value: string | number | null | undefined): number {
  if (value == null || value === "") {
    return 0;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const parsed = Number(value.replaceAll(",", ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

export const AddEditFacilityChemicalDialog: React.FC<AddEditFacilityChemicalDialogProps> = ({
  facilityChemicalId,
  reportingYear,
  onClose,
  reportId,
  alreadySelectedChemicalIds = [],
  facilityId,
}) => {
  const isAddMode = !facilityChemicalId;
  const open = !!facilityChemicalId || isAddMode;
  const { tenantId } = useTenant();
  const theme = useTheme();
  const alerts = useAlerts();
  const { data: facility } = useFacility(facilityId);
  const [previousYearData, setPreviousYearData] = useState<PreviousYearValuesQuery | null>(null);
  invariant(facilityId, "Facility id is required.");

  const [editFacilityChemical, { loading: submitting }] = useMutation(EDIT_FACILITY_CHEMICAL);

  const { data, loading } = useQuery<{
    facilityChemical: FacilityChemicalDetailsFragment & {
      previousYearMaxAmount?: number;
      previousYearUnit?: UnitType;
    };
  }>(FACILITY_CHEMICAL_DETAILS, {
    variables: {
      id: facilityChemicalId ?? "",
    },
    fetchPolicy: "cache-and-network",
    skip: isAddMode,
  });

  const { data: stateFieldsData } = useQuery(FACILITY_CHEMICAL_STATE_FIELDS_QUERY, {
    variables: {
      facilityId,
    },
    skip: !isAddMode || !!data?.facilityChemical?.stateFields.length,
  });

  const facilityState = data?.facilityChemical?.facility?.state ?? facility?.facility?.state;
  const isCalifornia = facilityState === "CA";

  const stateFields: DynamicField[] = useMemo(() => {
    const fields =
      data?.facilityChemical?.stateFields ??
      stateFieldsData?.facilityChemicalStateFieldsForFacility?.items ??
      [];
    const isCurrentReportingYear = reportingYear === currentTierIIReportingYear;

    if (isCurrentReportingYear) {
      // Note to AI: Label is always populated
      return fields.filter((field) => !field.label.includes(LEGACY_FIELD_SUFFIX));
    }

    return fields;
  }, [data?.facilityChemical?.stateFields, stateFieldsData, reportingYear]);

  const facilityChemical = useMemo(() => {
    return facilityChemicalId ? data?.facilityChemical : null;
  }, [data?.facilityChemical, facilityChemicalId]);

  const defaultValues: FacilityChemicalDetailFormType = useMemo(() => {
    return {
      id: facilityChemical?.id ?? uuid(),
      averageAmount: facilityChemical?.averageAmount,
      averageAmountCode: facilityChemical?.averageAmountCode ?? null,
      chemicalId: facilityChemical?.chemical?.id,
      storageLocations: (
        facilityChemical?.storageLocations ?? []
      ).map<FacilityChemicalStorageLocationDetailFormType>((sl) => ({
        id: sl.id,
        maxAmount: sl.maxAmount ?? null,
        averageAmount: sl.averageAmount ?? null,
        maxAmountLargestContainer: sl.maxAmountLargestContainer ?? null,
        pressure: sl.pressure ?? null,
        otherPressureValue: sl.otherPressureValue ?? null,
        storageType: sl.storageType ?? null,
        storageTypes: storageTypeFields(sl).storageTypes,
        storageTypeDescription: sl.storageTypeDescription ?? null,
        temperature: sl.temperature ?? null,
        unit: sl.unit ?? null,
        storageLocation: {
          id: sl.storageLocation?.id ?? uuid(),
          description: sl.storageLocation?.description ?? "",
          facilityId,
          latitude: sl.storageLocation?.latitude ?? null,
          longitude: sl.storageLocation?.longitude ?? null,
          OR_insideOutside: sl.storageLocation?.OR_insideOutside ?? null,
          OR_storageArea: sl.storageLocation?.OR_storageArea ?? null,
          OR_storageBuilding: sl.storageLocation?.OR_storageBuilding ?? null,
          OR_storageFloor: sl.storageLocation?.OR_storageFloor ?? null,
          OR_storageQuadrant: sl.storageLocation?.OR_storageQuadrant ?? null,
          OR_storageRoom: sl.storageLocation?.OR_storageRoom ?? null,
          NV_insideOutside: sl.storageLocation?.NV_insideOutside ?? null,
        },
      })),
      daysOnSite: facilityChemical?.daysOnSite,
      facilityId,
      hasConfidentialStorageLocations: facilityChemical?.hasConfidentialStorageLocations ?? null,
      maxAmount: facilityChemical?.maxAmount,
      maxAmountCode: facilityChemical?.maxAmountCode ?? null,
      maxAmountLargestContainer: facilityChemical?.maxAmountLargestContainer,
      reportingYear: facilityChemical?.reportingYear ?? reportingYear,
      unit: facilityChemical?.unit ?? (isAddMode ? UnitType.Pounds : null),
      isAlwaysReported: facilityChemical?.isAlwaysReported ?? false,
      facility: facilityChemical?.facility,
      stateFields:
        stateFields?.map((sf) => ({
          ...omit(sf, "selectOptions", "tooltip"),
          value: sf.value ?? null,
          type: ProgramArea.Epcra,
        })) ?? [],
      issues: facilityChemical?.issues,
    };
  }, [facilityChemical, facilityId, reportingYear, stateFields, isAddMode]);

  const form = useValidatingForm<FacilityChemicalDetailFormType>(
    defaultValues,
    facilityChemical?.issues ?? [],
    useFacilityChemicalInputValidation()
  );

  const { control, watch, handleSubmit, formState, issues, reset, getValues, setValue } = form;
  const {
    fields: storageLocations,
    append,
    replace,
    update,
  } = useFieldArray({
    control: control,
    name: "storageLocations",
    keyName: "_id",
  });

  const storageLocationsIssue = useMemo(() => {
    return issues.find((i) => i.key === "storageLocations");
  }, [issues]);

  const storageLocationsWithIssueCounts = useMemo(() => {
    return mapStorageLocationToIssueCount(storageLocations, issues);
  }, [issues, storageLocations]);

  const handleStorageLocationSubmit: SubmitHandler<FacilityChemicalStorageLocationInput> =
    useCallback(
      (data) => {
        const slIndex = storageLocations.findIndex((l) => l.id === data.id);
        if (slIndex >= 0) {
          update(slIndex, data);
        } else {
          append(data);
        }

        // Find all instances where the nested storageLocation id matches
        const matchingIndices = storageLocations
          .map((l, index) => (l.storageLocation.id === data.storageLocation.id ? index : -1))
          .filter((index) => index !== -1 && index !== slIndex);

        matchingIndices.forEach((index) => {
          // Update only the nested storageLocation while preserving other fields
          const updatedLocation = {
            ...storageLocations[index], // Keep other fields
            storageLocation: { ...data.storageLocation }, // Only update storageLocation part
          };
          update(index, updatedLocation); // Update each matching location
        });
      },
      [append, storageLocations, update]
    );

  const handleRemoveStorageLocation = useCallback(
    (storageLocationId: string) => {
      replace(storageLocations.filter((l) => l.id !== storageLocationId));
    },
    [replace, storageLocations]
  );

  // For CA facilities, derive chemical-level amounts from storage locations
  const watchedStorageLocations = watch("storageLocations");
  useEffect(() => {
    if (!isCalifornia || !watchedStorageLocations?.length) return;

    const sumMaxAmount = watchedStorageLocations.reduce(
      (sum, sl) => sum + toNumericOrZero(sl.maxAmount),
      0
    );
    const sumAverageAmount = watchedStorageLocations.reduce(
      (sum, sl) => sum + toNumericOrZero(sl.averageAmount),
      0
    );
    const maxLargestContainer = Math.max(
      ...watchedStorageLocations.map((sl) => toNumericOrZero(sl.maxAmountLargestContainer))
    );

    setValue("maxAmount", sumMaxAmount || null);
    setValue("averageAmount", sumAverageAmount || null);
    setValue("maxAmountLargestContainer", maxLargestContainer || null);
  }, [isCalifornia, watchedStorageLocations, setValue]);

  const handleClose = useCallback(() => {
    reset();
    setPreviousYearData(null);
    onClose();
  }, [onClose, reset]);

  const hasPendingChanges = formState.isDirty && !formState.isSubmitSuccessful;

  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const onCancel = useCallback(() => {
    // If there are pending changes, verify the user wants to close the dialog
    if (hasPendingChanges) {
      setShowConfirmClose(true);
      return;
    }

    handleClose();
  }, [handleClose, hasPendingChanges]);

  const onSubmit: SubmitHandler<FacilityChemicalDetailFormType> = async (data) => {
    try {
      const input = transform(data);

      await editFacilityChemical({
        variables: {
          id: isAddMode ? undefined : facilityChemical?.id,
          reportId,
          input,
        },
        refetchQueries: ["FacilityChemicals"].concat(
          reportId // Include report details if we are in a report context
            ? ["ReportingFacilityChemicals", GetReportDetailsQueryName]
            : []
        ),
      });
      handleClose();
      alerts.success("Successfully saved facility chemical");
    } catch (err) {
      alerts.error("An error occurred while saving facility chemical", err);
    }
  };

  const formData = watch();

  const [isOverThresholdInput] = useDebounce(
    omit(transform({ ...formData, storageLocations: storageLocations }), "stateFields"),
    500
  );

  const {
    data: isOverThresholdData,
    previousData: previousIsOverThresholdData,
    loading: thresholdLoading,
  } = useQuery(IS_OVER_THRESHOLD_QUERY, {
    variables: {
      input: isOverThresholdInput,
    },
    skip: !open || !isOverThresholdInput.chemicalId,
  });

  const { refetch: refetchPreviousYearData } = useQuery(PREVIOUS_YEAR_DETAILS_QUERY, {
    variables: {
      facilityId,
      chemicalId: data?.facilityChemical.chemical?.id ?? formData.chemicalId ?? "",
      reportingYear: reportingYear - 1,
    },
    skip: !formData.chemicalId && !data?.facilityChemical.chemical?.id,
    onCompleted: (data) => setPreviousYearData(data),
  });

  const hasPreviousYearData = useMemo(() => {
    return (
      previousYearData?.facilityChemicalByYear?.maxAmount &&
      previousYearData?.facilityChemicalByYear?.unit
    );
  }, [
    previousYearData?.facilityChemicalByYear?.maxAmount,
    previousYearData?.facilityChemicalByYear?.unit,
  ]);

  useEffect(() => {
    if (open && (formData.chemicalId || data?.facilityChemical.chemical?.id)) {
      refetchPreviousYearData().then((result) => setPreviousYearData(result.data));
    }
  }, [data?.facilityChemical.chemical?.id, formData.chemicalId, open, refetchPreviousYearData]);

  const thresholdData = useMemo(() => {
    return (
      isOverThresholdData?.getFacilityChemicalThresholdData ??
      previousIsOverThresholdData?.getFacilityChemicalThresholdData
    );
  }, [
    isOverThresholdData?.getFacilityChemicalThresholdData,
    previousIsOverThresholdData?.getFacilityChemicalThresholdData,
  ]);

  // if we are not open, handle close. Or, if we are open and not loading and the facility chemical is not found, handle close
  if (!open || (facilityChemicalId && !loading && !data?.facilityChemical)) {
    handleClose();
    return <></>;
  }

  return (
    <>
      <FormProvider {...form}>
        <form key={facilityChemicalId} onSubmit={handleSubmit(onSubmit)}>
          <Dialog
            open={open}
            onClose={onCancel}
            aria-labelledby="add-edit-facility-chemical-dialog-title"
            fullWidth
            maxWidth="lg"
            title={
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                <span>{isAddMode ? "Add" : "Edit"} Chemical Inventory</span>
                <ThresholdChip
                  isOverThreshold={thresholdData?.isOverThreshold}
                  errorMessage={thresholdData?.indeterminateReason}
                  loading={thresholdLoading || !thresholdData}
                />
                {facilityChemical?.chemical?.noHazardsNotReporting && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Chip
                      size="small"
                      label="Excluded from Tier II"
                      color="warning"
                      variant="outlined"
                    />
                    {chemicalExclusionWarning({
                      noHazardsNotReporting: true,
                      physicalHazards: facilityChemical.chemical.physicalHazards,
                      healthHazards: facilityChemical.chemical.healthHazards,
                    }) === "hazards_present" && (
                      <Tooltip title={CHEMICAL_EXCLUSION_HAZARDS_TOOLTIP}>
                        <WarningAmber color="warning" fontSize="small" />
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
            }
            additionalPrimaryActionElements={[
              <IssueListButton
                key="issues"
                issues={form.issues ?? facilityChemical?.issues ?? []}
              />,
            ]}
            primaryActionButtonProps={{
              loading: loading || submitting,
              onClick: handleSubmit(onSubmit),
              disabled: hasCriticalIssues(issues),
            }}
          >
            {loading ? (
              <Skelly />
            ) : (
              <>
                <CheckboxField
                  fullWidth={false}
                  name="isAlwaysReported"
                  label="Voluntarily report this chemical"
                  noErrorDisplay
                />

                <Grid container rowSpacing={2}>
                  <Grid size={12}>
                    <Controller
                      name="chemicalId"
                      control={control}
                      render={({ field, fieldState }) => (
                        <ChemicalPicker
                          {...field}
                          {...fieldState}
                          value={facilityChemical?.chemical}
                          defaultSearchTerm={`tenantId:${tenantId}`}
                          disabled={!isAddMode}
                          disabledChemicalsById={alreadySelectedChemicalIds}
                          disabledOptionLabel="You have already added this chemical to this facility"
                          onChange={(chem) => {
                            field.onChange(chem?.id);
                          }}
                          isOptionDisabled={(chemical) => !!chemical.facilityChemicals?.length}
                          facilityId={facilityId}
                          reportingYear={reportingYear}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={5}>
                    <Stack direction={"column"} spacing={1.5}>
                      <FormSelect
                        name="unit"
                        control={control}
                        label="Unit Type"
                        selectItems={Object.values(UnitType).map((unit) => ({
                          display: prettyPrintEnumValue(unit),
                          value: unit,
                        }))}
                      />
                      <Tooltip
                        title={getCaAmountTooltip(facilityState, "maxDailyAmount") ?? ""}
                        placement="top"
                      >
                        <span>
                          <FormTextField
                            name="maxAmount"
                            label="Max Daily Amount *"
                            control={control}
                            disabled={isCalifornia}
                            sx={{
                              marginBottom: theme.spacing(2), // this is due to the extra space needed for the helper text
                            }}
                            helperText={
                              isCalifornia
                                ? "For facilities in California, Maximum amount is derived from the total amount of all storage locations"
                                : hasPreviousYearData
                                  ? `Last Year: ${
                                      previousYearData?.facilityChemicalByYear?.maxAmount ?? ""
                                    } ${prettyPrintEnumValue(
                                      previousYearData?.facilityChemicalByYear?.unit ?? ""
                                    )}`
                                  : "Chemical not present at this facility last year"
                            }
                          />
                        </span>
                      </Tooltip>
                      <AmountCodeSelect
                        state={facilityState}
                        name="maxAmountCode"
                        label="Max Amount Code"
                        amount={watch("maxAmount")}
                        disabled={getValues("maxAmount") != null}
                      />
                      <Tooltip
                        title={getCaAmountTooltip(facilityState, "averageDailyAmount") ?? ""}
                        placement="top"
                      >
                        <span>
                          <FormTextField
                            name="averageAmount"
                            label="Avg Daily Amount *"
                            control={control}
                            disabled={isCalifornia}
                            helperText={
                              isCalifornia
                                ? "For facilities in California, Average amount is derived from storage locations"
                                : undefined
                            }
                          />
                        </span>
                      </Tooltip>
                      <AmountCodeSelect
                        state={facilityState}
                        name="averageAmountCode"
                        label="Avg Daily Amount Code"
                        amount={watch("averageAmount")}
                        disabled={getValues("averageAmount") != null}
                      />
                      <Tooltip
                        title={getCaAmountTooltip(facilityState, "maxAmountLargestContainer") ?? ""}
                        placement="top"
                      >
                        <span>
                          <FormTextField
                            name="maxAmountLargestContainer"
                            label="Max Amount in Largest Container"
                            control={control}
                            disabled={isCalifornia}
                            helperText={
                              isCalifornia
                                ? "For facilities in California, Largest container amount is derived from storage locations"
                                : undefined
                            }
                          />
                        </span>
                      </Tooltip>
                      <FormTextField name="daysOnSite" label="Days on Site" control={control} />
                    </Stack>
                  </Grid>
                  <Grid
                    size={7}
                    sx={{
                      display: "flex",
                      flex: 1,
                      flexDirection: "column",
                      paddingLeft: theme.spacing(2),
                    }}
                  >
                    <div
                      id="storage-location-data-grid-portal"
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    ></div>
                    <RadioGroupField
                      name="hasConfidentialStorageLocations"
                      label="This chemical has confidential storage locations"
                      control={control}
                      radioOptions={[
                        {
                          label: "Yes",
                          value: true,
                        },
                        {
                          label: "No",
                          value: false,
                        },
                      ]}
                    />
                  </Grid>
                </Grid>
                {stateFields.length !== 0 && (
                  <StateFieldsForm
                    namePrefix="stateFields"
                    openJurisdictions={facilityChemical?.jurisdictions ?? []}
                    context={"facilityChemical"}
                    fields={stateFields}
                    sx={{ p: theme.spacing(3) }}
                  />
                )}
              </>
            )}
            <CatalogLink
              id={facilityChemical?.chemical?.id ?? ""}
              type="Chemical"
              name={facilityChemical?.chemical?.name ?? ""}
              issues={facilityChemical?.issues ?? []}
            />
          </Dialog>
        </form>
      </FormProvider>
      {open && !loading ? (
        /* NOTE: This component is rendered using a React Portal so that we do not
      have nested forms in the React component hierarchy. With nested forms,
      react-hook-form will submit both forms at the same time which we do not want. */
        <StorageLocationsDataGrid
          facilityId={defaultValues.facilityId || ""}
          facilityChemicalId={defaultValues.id ?? ""}
          storageLocations={storageLocationsWithIssueCounts}
          sx={{ mb: theme.spacing(4), flex: 1 }}
          disabled={false}
          handleRemoveStorageLocation={handleRemoveStorageLocation}
          handleStorageLocationSubmit={handleStorageLocationSubmit}
          issue={storageLocationsIssue}
          displayIssueCountColumn
          facilityChemical={formData}
        />
      ) : (
        ""
      )}
      <ConfirmDialog
        open={showConfirmClose}
        onConfirm={handleClose}
        onClose={() => setShowConfirmClose(false)}
        header="Are you sure you want to leave? You have unsaved changes."
        confirmText="Yes, I'm Sure"
      />
    </>
  );
};

function mapStorageLocationToIssueCount(
  storageLocations: FacilityChemicalStorageLocationInput[],
  allIssues: Issue[]
): FacilityChemicalStorageLocationInputWithIssues[] {
  return storageLocations.map((sl) => {
    const issues = allIssues.filter(
      (i) => i.modelId === sl.id || i.modelId === sl.storageLocation?.id
    );
    const issueCount = issues.length;
    return { ...sl, issueCount, issues };
  });
}

function Skelly() {
  const theme = useTheme();
  return (
    <Grid container rowSpacing={2} marginTop={1}>
      <Grid size={12}>
        <Skeleton variant="rectangular" height={68} />
      </Grid>
      <Grid size={6}>
        <Skeleton variant="rectangular" height={68} />
        <Skeleton variant="rectangular" height={68} sx={{ marginTop: 2 }} />
        <Skeleton variant="rectangular" height={68} sx={{ marginTop: 2 }} />
        <Skeleton variant="rectangular" height={68} sx={{ marginTop: 2 }} />
        <Skeleton variant="rectangular" height={68} sx={{ marginTop: 2 }} />
        <Skeleton variant="rectangular" height={68} sx={{ marginTop: 2 }} />
        <Skeleton variant="rectangular" height={68} sx={{ marginTop: 2 }} />
      </Grid>
      <Grid
        size={6}
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          paddingLeft: theme.spacing(2),
        }}
      >
        <div
          id="storage-location-data-grid-portal"
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Skeleton variant="rectangular" height="100%" />
        </div>
        <Skeleton variant="rectangular" height={60} sx={{ marginTop: 2 }} />
      </Grid>
    </Grid>
  );
}
